// playbookItemFocusUtils.js

import { getChecklistTemplateItemLabel } from 'utilities/ChecklistTemplateItems';
import { readWorkflowTaskChecklistRecipe } from 'node/missions/mission/utils/workflowStepChecklistUtils';
import { getPlaybookFocusSteps } from 'node/missions/mission/focus/playbookFocusUtils';

/**
 * getPlaybookItemFocusEntryKey
 *
 * Stable key for a flattened checklist item on a playbook path.
 *
 * @param {object|null} entry - Item focus entry.
 * @returns {string} Entry key.
 */
export function getPlaybookItemFocusEntryKey(entry) {
  const stepKey = String(entry?.step?.task?.key || '').trim();
  const itemKey = String(entry?.itemKey || '').trim();
  if (!stepKey || !itemKey) return '';
  return `${stepKey}:${itemKey}`;
}

/**
 * getPlaybookItemFocusEntries
 *
 * Flattens authored checklist items across the active playbook path.
 *
 * @param {object} options
 * @param {object|null} options.missionRun - Normalized Mission run.
 * @param {object|null} options.template - Playbook template.
 * @param {Map<string, object>|null} [options.stepNodesByKey] - Live TASK nodes.
 * @returns {object[]} Item descriptors in path order.
 */
export function getPlaybookItemFocusEntries({
  missionRun = null,
  template = null,
  stepNodesByKey = null,
} = {}) {
  const steps = getPlaybookFocusSteps(missionRun);
  const templateTasks = Array.isArray(template?.tasks) ? template.tasks : [];
  const entries = [];

  steps.forEach((step) => {
    const taskKey = String(step?.task?.key || '').trim();
    if (!taskKey) return;

    const templateTask = templateTasks.find((entry) => (
      String(entry?.key || '').trim() === taskKey
    )) || null;
    const recipe = readWorkflowTaskChecklistRecipe(step.task, templateTask);
    if (!recipe.items.length) return;

    const stepNode = step?.task?.nodeKey && stepNodesByKey instanceof Map
      ? stepNodesByKey.get(String(step.task.nodeKey)) || null
      : null;

    recipe.items.forEach((recipeItem, itemIndex) => {
      const itemKey = String(recipeItem?.key || '').trim();
      if (!itemKey) return;

      entries.push({
        itemIndex,
        itemKey,
        recipeItem,
        step,
        stepItemCount: recipe.items.length,
        stepNode,
        task: step.task,
        text: getChecklistTemplateItemLabel(recipeItem) || 'Untitled item',
      });
    });
  });

  return entries;
}

/**
 * isPlaybookItemFocusDone
 *
 * @param {object|null} entry - Item focus entry.
 * @param {Map<string, object>} statusByKey - Live status by entry key.
 * @returns {boolean} Whether the item is done.
 */
export function isPlaybookItemFocusDone(entry, statusByKey) {
  const key = getPlaybookItemFocusEntryKey(entry);
  if (!key) return false;

  const live = statusByKey instanceof Map ? statusByKey.get(key) : null;
  if (live && typeof live === 'object') {
    return live.done === true;
  }

  return String(entry?.task?.status || '').trim() === 'complete';
}

/**
 * getPlaybookCurrentItemIndex
 *
 * Lands on the first incomplete item of the process-current step,
 * else the first incomplete item on the path, else the last item.
 *
 * @param {object[]} entries - Flattened item descriptors.
 * @param {Map<string, object>} statusByKey - Live status by entry key.
 * @param {number} [currentStepIndex=-1] - Process-current step index.
 * @returns {number} Current item index, or -1 when empty.
 */
export function getPlaybookCurrentItemIndex(
  entries,
  statusByKey,
  currentStepIndex = -1,
) {
  if (!Array.isArray(entries) || entries.length === 0) return -1;

  if (Number.isFinite(currentStepIndex) && currentStepIndex >= 0) {
    const currentStepItem = entries.findIndex((entry) => (
      Number(entry?.step?.index) === currentStepIndex
      && !isPlaybookItemFocusDone(entry, statusByKey)
    ));
    if (currentStepItem >= 0) return currentStepItem;
  }

  const firstOpen = entries.findIndex((entry) => (
    !isPlaybookItemFocusDone(entry, statusByKey)
  ));
  if (firstOpen >= 0) return firstOpen;

  return entries.length - 1;
}

/**
 * getPlaybookItemFocusStepLabel
 *
 * @param {object|null} entry - Item focus entry.
 * @returns {string} Step position sentence.
 */
export function getPlaybookItemFocusStepLabel(entry) {
  const position = Number(entry?.step?.position);
  const total = Number(entry?.step?.total);
  const title = String(entry?.task?.title || entry?.step?.task?.title || '').trim();
  if (!Number.isFinite(position) || !Number.isFinite(total) || total < 1) {
    return title;
  }

  const prefix = `Step ${position} of ${total}`;
  return title ? `${prefix} · ${title}` : prefix;
}

/**
 * getPlaybookItemFocusItemLabel
 *
 * @param {object|null} entry - Item focus entry.
 * @returns {string} Item position on the current step.
 */
export function getPlaybookItemFocusItemLabel(entry) {
  const itemIndex = Number(entry?.itemIndex);
  const stepItemCount = Number(entry?.stepItemCount);
  if (!Number.isFinite(itemIndex) || !Number.isFinite(stepItemCount) || stepItemCount < 1) {
    return '';
  }

  return `Item ${itemIndex + 1} of ${stepItemCount}`;
}

/**
 * getPlaybookItemFocusPeekLabel
 *
 * @param {object|null} entry - Neighbor entry.
 * @param {Map<string, object>} statusByKey - Live status by entry key.
 * @param {'previous'|'next'} role - Which neighbor is being labeled.
 * @returns {string} Peek label.
 */
export function getPlaybookItemFocusPeekLabel(entry, statusByKey, role) {
  const text = String(entry?.text || '').trim() || 'Untitled item';
  if (role !== 'previous') return text;
  if (isPlaybookItemFocusDone(entry, statusByKey)) return `${text} · done`;
  return text;
}
